/**
 * Domain model of the FWP backend. These types travel over the wire (JSON) and are stored by the server.
 * All timestamps are epoch milliseconds. Player ids are the backend's own ids (never VK ids).
 */
import type { JsonValue } from '../json';
import type { CommandLogData } from '../../session/command-log';
import { DEFAULT_ONLINE_MAP, type OnlineBattleInit, type OnlineMapId, onlineMapOf } from '../../session/online';

export type { OnlineBattleInit, OnlineBattleTeam, OnlineMapId } from '../../session/online';

export type PlayerId = string;
/** Epoch milliseconds. */
export type Timestamp = number;

/**
 * The player id that stands for a deleted account in records the server keeps for OTHER players (an online battle's
 * participants and teams in `session.get` and in replays): `account.delete` and the operator's deletion replace the
 * deleted player's id with it, and its name with DELETED_PLAYER_NAME. It is never the id of a real account (player ids
 * are `p_` + 16 characters of an alphabet without `l`), so a client can show its own text for it.
 */
export const DELETED_PLAYER_ID = 'p_deleted';
/** The display name that replaces a deleted player's name in records kept for other players (see DELETED_PLAYER_ID). */
export const DELETED_PLAYER_NAME = '[deleted]';

// ---------------------------------------------------------------------------------------------------------
// Accounts and auth

export interface BanInfo {
  /** Moderation code; the original BAN_DESC codes (101-103, 201-202) may be reused (FWP.as:10471-10475). */
  code: number;
  reason: string;
  /** Epoch ms, or null for a permanent ban. */
  until: Timestamp | null;
}

/**
 * The client installation a session belongs to, as the client describes itself (every auth request accepts it; shown
 * by `auth.sessions`). Plain bounded strings, no browser-specific fields: web, Android and iOS clients send the same.
 */
export interface DeviceInfo {
  /** Lower-case platform identifier, e.g. 'web', 'android', 'ios', 'desktop' ('unknown' when the client sent none). */
  platform: string;
  /** Device model, e.g. 'Pixel 8' (optional). */
  model?: string;
  /** Client application version (optional). */
  appVersion?: string;
  /** User-chosen name of the device (optional). */
  label?: string;
}

/** The result of `account.delete`: the account that no longer exists and when it was deleted. */
export interface AccountDeletedResult {
  playerId: PlayerId;
  deletedAt: Timestamp;
}

/** The account behind an auth result. */
export interface AccountInfo {
  playerId: PlayerId;
  displayName: string;
  /** Lower-case login, null for a guest account. */
  username: string | null;
  /** True while the account has no username/password credentials (it is reachable through its device token only). */
  guest: boolean;
  createdAt: Timestamp;
}

/**
 * Stable `details.reason` values of UNAUTHORIZED errors:
 * - 'session_required': the request carried no credentials (no bearer token, the connection is not bound);
 * - 'invalid_token': the access / refresh / legacy token is unknown;
 * - 'token_expired': the access token expired: call `auth.refresh` with the refresh token, then repeat the request;
 * - 'refresh_expired': the refresh token expired (the device session ended): sign in again;
 * - 'revoked': the session was ended (logout, `auth.revoke`, the session limit, a detected refresh token reuse);
 * - 'refresh_reused': an already rotated refresh token was presented again: the session is revoked (theft detection);
 * - 'session_replaced': the session was ended because the account signed in (or resumed another session) somewhere
 *   else (one active session per account): tell the player and offer to sign in again here, which ends the other one;
 *   never sign in again by itself;
 * - 'refresh_rotated': the previous refresh token was presented again within the short grace window after its
 *   rotation (another window of the same device refreshed first): nothing is revoked; take the newer stored pair;
 * - 'bad_credentials': wrong username or password (auth.login);
 * - 'invalid_credentials': auth.signIn with a nickname no account has or a wrong password (one answer for both, with
 *   comparable timing: nobody learns from it whether a nickname exists).
 */
export const AUTH_FAILURE_REASONS = [
  'session_required',
  'invalid_token',
  'token_expired',
  'refresh_expired',
  'revoked',
  'refresh_reused',
  'session_replaced',
  'refresh_rotated',
  'bad_credentials',
  'invalid_credentials',
] as const;
export type AuthFailureReason = (typeof AUTH_FAILURE_REASONS)[number];

/**
 * WebSocket close codes the server uses (docs/api.md §2.2):
 * - sessionEnded 4001: the connection's device session was ended (logout elsewhere, auth.revoke, the session limit, a
 *   detected refresh token reuse, the account's deletion): sign in again;
 * - banned 4003: the operator banned the player (the next request or auth.resume answers BANNED);
 * - sessionReplaced 4004: the account signed in (or resumed another session) somewhere else, which ended this
 *   connection's session (one active session per account). A client must NOT reconnect and resume by itself: its
 *   tokens answer UNAUTHORIZED 'session_replaced'. It tells the player and signs in again only when asked to (which
 *   ends the other session in turn).
 */
export const WS_CLOSE_CODES = { sessionEnded: 4001, banned: 4003, sessionReplaced: 4004 } as const;

/**
 * Result of every auth request that signs a device in. Bearer credentials only (no cookies): the short-lived access
 * token authorizes requests (`Authorization: Bearer <accessToken>` over HTTP; over a WebSocket or the in-process
 * transport the connection is bound to it by the auth request itself or by `auth.resume`); the long-lived refresh
 * token obtains a new pair through `auth.refresh` and is rotated on every use.
 */
export interface AuthResult {
  playerId: PlayerId;
  displayName: string;
  /** Short-lived bearer access token. */
  accessToken: string;
  /** Expiry of the access token (server time). */
  accessExpiresAt: Timestamp;
  /**
   * Long-lived refresh token (`auth.refresh`), a new one on every sign-in and refresh; the client stores it securely.
   * Absent when `auth.resume` re-bound a connection to an existing access token (the stored refresh token stays valid).
   */
  refreshToken?: string;
  /** Expiry of the refresh token, i.e. of the device session unless it is refreshed (server time). */
  refreshExpiresAt?: Timestamp;
  /** Public id of the device session (`auth.sessions`, `auth.revoke`); not a credential. */
  sessionId: string;
  account: AccountInfo;
  /** Server time of the response (clients estimate the expiry on their own clock with it). */
  serverTime: Timestamp;
  isNew: boolean;
  /** Returned once when a new guest device token was issued; the client must persist it. */
  deviceToken?: string;
  /** Username when the account has credentials. */
  username: string | null;
  /** @deprecated Protocol 1 name of `accessToken` (kept for existing clients). */
  token: string;
  /** @deprecated Protocol 1 name of `accessExpiresAt` (kept for existing clients). */
  expiresAt: Timestamp;
}

/** An auth result that issued a new token pair (guest, register, login, link, refresh). */
export interface IssuedAuthResult extends AuthResult {
  refreshToken: string;
  refreshExpiresAt: Timestamp;
}

/**
 * The player as the game's account screens show it (`auth.signUp`, `auth.signIn`, `auth.session`, `profile.me`). The
 * level and experience come from the player's canonical game state.
 */
export interface PlayerCard {
  /** Stable id of the player (never changes, also not with a new nickname or display name). */
  playerId: PlayerId;
  /**
   * The nickname as the player typed it (case kept) for accounts made by auth.signUp; the display name for guest and
   * username accounts.
   */
  nickname: string;
  level: number;
  exp: number;
  /** True for a guest account (no nickname/username and password: reachable through its device token only). */
  guest: boolean;
  createdAt: Timestamp;
  /** The public projection of the game state (leaderboards), once the player has a game state. */
  summary?: ProfileSummary;
}

/** Result of `auth.signUp` / `auth.signIn`: the new device session's tokens plus the player. */
export interface SignedInResult extends IssuedAuthResult {
  player: PlayerCard;
}

/** The device session of the asking connection (`auth.session`). */
export interface CurrentSessionInfo {
  sessionId: string;
  device: DeviceInfo;
  createdAt: Timestamp;
  /** Last request or refresh of the session (updated at most once a minute). */
  lastSeenAt: Timestamp;
  /** Expiry of the access token this request was authorized with. */
  accessExpiresAt: Timestamp;
  /** Expiry of the session's refresh token: the session ends then unless it is refreshed. */
  refreshExpiresAt: Timestamp;
}

/** One device session of the account (`auth.sessions`). */
export interface AuthSessionInfo {
  sessionId: string;
  device: DeviceInfo;
  createdAt: Timestamp;
  /** Last request or refresh of the session (updated at most once a minute). */
  lastSeenAt: Timestamp;
  /** The session of the connection that asked. */
  current: boolean;
}

// ---------------------------------------------------------------------------------------------------------
// Profiles

export type SaveReason = 'action' | 'battle_start' | 'battle_end' | 'reset' | 'tribute' | 'import' | 'grant' | 'sync';

export const SAVE_REASONS: readonly SaveReason[] = ['action', 'battle_start', 'battle_end', 'reset', 'tribute', 'import', 'grant', 'sync'];

/**
 * Public, indexable projection of the opaque game state, used for leaderboards and public profiles.
 * The server derives it from `gameState` when the fields are recognizable (original `player` field names),
 * otherwise it takes the values the client supplied with `profile.save`.
 */
export interface ProfileSummary {
  level: number;
  exp: number;
  /** Displayed rating: `rating + achiev_rating_bonus` (new_interf.as:4201). */
  rating: number;
  /** Successful zone captures (`achiev.assault`). */
  assaults: number;
  attack: number;
  protect: number;
  health: number;
  worms: number;
  spec: number;
  careerMax: number;
  missionsMax: number;
  survivalMax: number;
  bossMax: number;
}

export const PROFILE_SUMMARY_KEYS: readonly (keyof ProfileSummary)[] = [
  'level',
  'exp',
  'rating',
  'assaults',
  'attack',
  'protect',
  'health',
  'worms',
  'spec',
  'careerMax',
  'missionsMax',
  'survivalMax',
  'bossMax',
];

export function emptyProfileSummary(): ProfileSummary {
  return {
    level: 1,
    exp: 0,
    rating: 0,
    assaults: 0,
    attack: 0,
    protect: 0,
    health: 0,
    worms: 0,
    spec: 0,
    careerMax: 1,
    missionsMax: 1,
    survivalMax: 1,
    bossMax: 1,
  };
}

/**
 * Facts of the session entry that are not part of the game state (the original kept them in the client's memory),
 * returned by `profile.load` when the server ran the entry (trust level L2).
 */
export interface ProfileVolatile {
  /** Papa John's shop is open this hour (FWP.PAPA_HERE). */
  papaHere: boolean;
  /** This load started a new calendar day of the player (login streak, daily counters). */
  newDay: boolean;
  /** A daily present is waiting to be claimed (`player.command` present.claim). */
  presentAvailable: boolean;
  /** Login streak the waiting present is for (FWP.get_present; 0 = no present). */
  present: number;
  /** Time zone the server computes the player's calendar in (minutes east of UTC; see `profile.load`). */
  tzOffsetMin: number;
}

/** Op-specific facts of a command response that are not part of the game state. */
export type CommandVolatile = { [key: string]: JsonValue };

/**
 * Response of `player.command`: the canonical game state after the command (the client adopts it as a whole), the
 * op's result (values the screens show, e.g. a roulette prize) and the profile revision.
 */
export interface PlayerCommandResult {
  revision: number;
  /** The whole canonical game state (`PlayerProfile.gameState` format). */
  gameState: JsonValue;
  result: JsonValue;
  serverTime: Timestamp;
  volatile?: CommandVolatile;
}

// ---------------------------------------------------------------------------------------------------------
// Results of the service-wrapped player.command ops (their inputs come from the server's own state)

/** A grave an enemy worm left on the map (`grave.search` index = position in the list + 1). */
export interface BattleGraveInfo {
  name: string;
  /** cpu_level of the dead enemy's team. */
  cpu_level: number;
}

/** Modes of a solo battle against the game's own teams (`battle.start`). */
export type SoloBattleModeName = 'career' | 'missions' | 'survival' | 'boss' | 'assault';

/**
 * How a solo battle ended as the game observed it (end_game_fn's inputs): the `claim` of `battle.finish`. A win
 * (winner 1) needs every counter; a loss may leave them out.
 */
export interface BattleOutcomeClaim {
  /** Team that won (1 = the player; 0 = cancelled / nobody). */
  winner: number;
  mode: BattleMode;
  /** career / missions / survival / boss: the stage. */
  stage?: number;
  /** assault: the zone. */
  zone?: number;
  /** Enemy worms the player killed, by the killing weapon's type. */
  ach_killed?: { p: number; e: number; a: number; c: number; h: number; f: number; l: number };
  ach_paralyzed?: number;
  ach_annihilated?: number;
  /** A worm of the player was injured. */
  ach_injure?: boolean;
  /** A worm of the player died. */
  ach_kill?: boolean;
  /** Enemy graves left on the map, in object order. */
  graves?: BattleGraveInfo[];
}

/** `player.command` 'battle.start' arguments: a stage of a mode, or an assault zone. */
export interface BattleStartCommandArgs {
  mode: SoloBattleModeName;
  /** career / missions / survival / boss. */
  stage?: number;
  /** assault: zone 1..17 (its stage and owner come from the server). */
  zone?: number;
  /** Display name of the player's team in the battle (default: the account's display name). */
  teamName?: string;
}

/** `battle.start` result: everything the client starts the battle with (the server keeps the same as the battle's init). */
export interface BattleStartCommandResult {
  battleId: string;
  mode: SoloBattleModeName;
  /** The stage, or the zone of an assault. */
  stage: number;
  /** assault: the zone's stage (enemy strength) when the battle started. */
  zoneStage?: number;
  /** Battle seed 0..399. */
  seed: number;
  /** Stimulants taken into the battle, id -> count (removed from the state). */
  stimsInBattle: Record<string, number>;
  /** Auction annihilators the player holds for this battle. */
  annihilators: number;
  teamName: string;
  /** Papa John is in town this hour. */
  papaHere: boolean;
  tired: { before: number; after: number };
  /** A battle that was still pending and ended as a loss by this start (its start costs stay). */
  endedBattle?: { battleId: string; reason: 'replaced' | 'abandoned' };
}

/** `player.command` 'battle.finish' arguments. */
export interface BattleFinishCommandArgs {
  /** BattleStartCommandResult.battleId. */
  battleId: string;
  claim: BattleOutcomeClaim;
  /** The battle's whole input in global battle ticks. */
  log: CommandLogData;
}

/** What a battle granted (a flat view of the end-game screen). */
export interface BattleGranted {
  gold: number;
  exp: number;
  /** Rating of the win plus 100 per level gained. */
  rating: number;
  levels: number;
  diam: number;
  skillPts: number;
  perkPts: number;
  /** Surprise perk weapons, one per level gained. */
  weapons: string[];
}

/**
 * `battle.finish` result. `verdict`: 'verified' = the server re-simulated the battle and got the claim; 'rejected' = the
 * claim was not accepted: the simulation differs from it (the simulated outcome was applied), the record cannot be
 * re-simulated (input the game could not have produced, no end within the tick limit) or the battle was abandoned
 * (both: the battle is lost, winner 0); 'unverified' = the server has no simulator (the claim was applied). A server
 * with a simulator that cannot answer now replies UNAVAILABLE { reason: 'simulation_unavailable' }: the battle stays
 * pending, retry with the same opId.
 */
export interface BattleFinishCommandResult {
  battleId: string;
  verdict: ValidationVerdict;
  /** Winner of the outcome the server applied. */
  winner: number;
  win: boolean;
  /**
   * The outcome gave nothing because the battle no longer matches the state ('battle_state_changed': the stage
   * progress changed since the start; 'battle_mismatch': the outcome is not this battle's mode / stage;
   * 'abandoned': the battle started more than 6 h ago and is lost).
   */
  refused?: 'battle_state_changed' | 'battle_mismatch' | 'abandoned';
  /** The game's end-game object (win / new level / surprise screens); null when refused. */
  endGame: { [key: string]: JsonValue } | null;
  granted: BattleGranted;
  /** Enemy graves for `grave.search` (only after a won battle that is not an assault). */
  graves: BattleGraveInfo[];
  /** assault win: the zone was captured. */
  captured?: boolean;
  /** assault win without a capture: why ('own' / 'clan': the zone belongs to the player or the clan by now). */
  captureBlocked?: 'own' | 'clan' | 'stage_changed' | 'not_verified';
  /** The player's level afterwards. */
  level: number;
  /** The stored replay of the battle. */
  replayId: string;
}

/** `battle.forfeit` result: the pending battle ended as a loss (its start costs stay). */
export interface BattleForfeitCommandResult {
  battleId: string;
  forfeited: true;
}

/** `grave.search` result: what the grave held ('' = nothing; 'stim' | 'gold' | 'diam' | 'obj' | 'weapon'). */
export interface GraveSearchCommandResult {
  index: number;
  ptype: string;
  /** Item id, or the amount for gold / diamonds. */
  id: string;
}

/** `present.claim` result: the daily present of the login streak `tween`. */
export interface PresentClaimCommandResult {
  tween: number;
  gold: number;
  diam: number;
  skill_pts: number;
}

/** `tribute.claim` result: gold of the own zones and the clan's zones, credited to the state. */
export interface TributeClaimCommandResult {
  /** Zones the player holds. */
  terr_total: number;
  my_gold: number;
  clans_gold: number;
  /** my_gold + clans_gold. */
  gold: number;
  /**
   * The next claim is possible after this moment (epoch ms; the game state keeps it as next_assault_payment in unix
   * seconds). A claim before it: VALIDATION_FAILED 'not_available' with details { problem, nextPaymentAt }.
   */
  nextPaymentAt: Timestamp;
}

export interface LegacyImport {
  format: 'v45' | 'v45-short';
  /** The validated legacy save string; the game decodes it with its ported read_storage. */
  data: string;
  importedAt: Timestamp;
}

export interface PlayerProfile {
  id: PlayerId;
  displayName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  /** Optimistic-concurrency revision; 0 = never saved. */
  revision: number;
  schemaVersion: 1;
  /** Opaque serialized game `player` object owned by src/game; `null` until the first save. */
  gameState: JsonValue | null;
  summary: ProfileSummary;
  /** Pending legacy save waiting to be decoded by the game (see profile.importLegacy). */
  legacyImport: LegacyImport | null;
}

/** Team parameters of a player as seen by others (assault defenders, public profile). */
export interface TeamSnapshot {
  playerId: PlayerId;
  displayName: string;
  level: number;
  attack: number;
  protect: number;
  health: number;
  worms: number;
  spec: number;
  /** PERKS_DEF key -> level. */
  perks: Record<string, number>;
  /** WEAPONS_DEF key -> number of owned copies. */
  weapons: Record<string, number>;
  wormNames: string[];
  implant: number;
  flag: number;
  takenAt: Timestamp;
}

/** Client-supplied team data (the server fills identity fields and timestamps). */
export type TeamSnapshotInput = Partial<Omit<TeamSnapshot, 'playerId' | 'displayName' | 'takenAt'>>;

export interface PublicProfile {
  playerId: PlayerId;
  displayName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  /** The player's clan (null = none); `clan` carries its name and emblem. */
  clanId: number | null;
  clan: ClanBrief | null;
  badges: string[];
  summary: ProfileSummary;
  team: TeamSnapshot | null;
}

/**
 * Server -> game delivery of items/currency for the client-owned state (trust levels L0/L1): the game applies pending
 * grants to its state and acknowledges them atomically with `profile.save` (`consumedGrants`), so a grant is never
 * applied twice. At L2 the server changes the canonical state itself (operator gifts: the admin tool), so no grants
 * are created. The `auction_*` sources belong to an earlier auction design; such stored grants are only listed.
 */
export interface Grant {
  grantId: string;
  playerId: PlayerId;
  createdAt: Timestamp;
  source: 'auction_win' | 'auction_refund' | 'auction_sale' | 'operator';
  kind: 'item' | 'currency';
  itemKind?: string;
  itemId?: string;
  quantity?: number;
  currency?: Currency;
  amount?: number;
  note?: string;
  refId?: string;
}

// ---------------------------------------------------------------------------------------------------------
// Leaderboards

export type LeaderboardBoard = 'level' | 'rating' | 'assaults';
export const LEADERBOARD_BOARDS: readonly LeaderboardBoard[] = ['level', 'rating', 'assaults'];

export interface LeaderboardEntry {
  rank: number;
  playerId: PlayerId;
  displayName: string;
  level: number;
  exp: number;
  rating: number;
  assaults: number;
  /** The player's specialisation (0: none yet; 1-4: ProfileSummary.spec): the portrait the tops show beside the name. */
  spec: number;
  /** The player's clan (null = none); `clan` carries its name and emblem. */
  clanId: number | null;
  clan: ClanBrief | null;
}

export interface LeaderboardPage {
  board: LeaderboardBoard;
  page: number;
  pageSize: number;
  totalPages: number;
  totalEntries: number;
  entries: LeaderboardEntry[];
  me: LeaderboardEntry | null;
  /** The same entries as `entries` (the uniform list shape of every paginated request). */
  items: LeaderboardEntry[];
  /** Cursor of the following page (null = last page); pass it back as `cursor`. */
  next: string | null;
}

// ---------------------------------------------------------------------------------------------------------
// Assault (zone capture) and clans

export interface AssaultZone {
  /** 1..zoneCount (17 in the original: TEAMS_DEF.assault1..17). */
  zone: number;
  /** Number of captures (`zone_stage`), scales the enemy team (get_assault_multi, FWP.as:19475). */
  stage: number;
  ownerId: PlayerId | null;
  ownerName: string | null;
  /** The owner's clan now (a clan holds the zones of its current members), null = none; `ownerClan` = its brief. */
  ownerClanId: number | null;
  ownerClan: ClanBrief | null;
  /** 0 when never captured. */
  lastCaptureAt: Timestamp;
}

export interface AssaultTarget {
  zone: AssaultZone;
  /** Snapshot of the owner's team at capture time (usable by the assault mode), null for unowned zones. */
  defender: TeamSnapshot | null;
  canAttack: boolean;
  blockedReason: 'own' | 'clan' | null;
  /** Hourly tribute of the zone (TEAMS_DEF.assault<z>.payments). */
  tributePerHour: number;
}

export interface TributeResult {
  myGold: number;
  clanGold: number;
  total: number;
  nextPaymentAt: Timestamp;
  serverTime: Timestamp;
  zones: { zone: number; gold: number; share: 'own' | 'clan' }[];
}

/** A clan member's role: exactly one leader, at most limits.clans.maxOfficers officers, the others members. */
export type ClanRole = 'leader' | 'officer' | 'member';
export const CLAN_ROLES: readonly ClanRole[] = ['leader', 'officer', 'member'];
/** How players get into a clan: 'open' = clan.join at once, 'request' = clan.request and a decision (clan.decide). */
export type ClanJoinPolicy = 'open' | 'request';
export const CLAN_JOIN_POLICIES: readonly ClanJoinPolicy[] = ['open', 'request'];

/** What a client needs to draw a clan next to a player or a zone. */
export interface ClanBrief {
  clanId: number;
  name: string;
  /** Frame of the game's clan_icons sprite (one of limits.clans.emblems). */
  emblem: number;
}

export interface ClanSummary {
  clanId: number;
  name: string;
  /** Sum of the successful captures of the current members (all of their captures, as in the original). */
  rating: number;
  memberCount: number;
  emblem: number;
  joinPolicy: ClanJoinPolicy;
  /** Zones the members hold now. */
  zoneCount: number;
  maxMembers: number;
}

/** A zone a clan holds: one of its members owns it. */
export interface ClanZone {
  zone: number;
  ownerId: PlayerId;
  ownerName: string;
  stage: number;
  lastCaptureAt: Timestamp;
  /** Hourly tribute of the zone (the owner gets it in full, the other members a tenth). */
  tributePerHour: number;
}

/** The clan card: the summary, the description, the leader and the zones the members hold. */
export interface ClanInfo extends ClanSummary {
  description: string;
  leaderId: PlayerId;
  leaderName: string;
  createdAt: Timestamp;
  zones: ClanZone[];
}

export interface ClanMember {
  playerId: PlayerId;
  displayName: string;
  level: number;
  captures: number;
  role: ClanRole;
  joinedAt: Timestamp;
}

/** A pending request to join a clan whose policy is 'request'. */
export interface ClanJoinRequest {
  clanId: number;
  clanName: string;
  playerId: PlayerId;
  displayName: string;
  level: number;
  at: Timestamp;
}

/** Kinds of the clan history (ClanEvent.type). */
export type ClanEventType =
  | 'created'
  | 'renamed'
  | 'settings'
  | 'joined'
  | 'left'
  | 'kicked'
  | 'role'
  | 'leader'
  | 'leader_auto'
  | 'zone_captured'
  | 'zone_lost'
  | 'member_deleted'
  | 'moderated';
export const CLAN_EVENT_TYPES: readonly ClanEventType[] = [
  'created',
  'renamed',
  'settings',
  'joined',
  'left',
  'kicked',
  'role',
  'leader',
  'leader_auto',
  'zone_captured',
  'zone_lost',
  'member_deleted',
  'moderated',
];

/** What an operator did to a clan (ClanEvent.action of 'moderated'; the operator is never named). */
export type ClanModerationAction = 'rename' | 'description' | 'remove' | 'leader';

/**
 * One event of a clan's history. Structured: every client composes the text in its own language from the type and the
 * fields. Player names are the current ones (read when the history is read); a deleted player is DELETED_PLAYER_ID /
 * DELETED_PLAYER_NAME. The fields of each type:
 *   created (actor), renamed (actor, from, to), settings (actor, emblem?, joinPolicy?, description?: true),
 *   joined (actor = the new member, via; byId / byName = who accepted the request), left (actor, zones),
 *   kicked (actor, target, zones), role (actor, target, role), leader (actor = the old leader, target = the new one),
 *   leader_auto (target = the new leader, reason), zone_captured (actor, zone, stage),
 *   zone_lost (target = the member who lost it, zone, stage; byId / byName = the capturer, byClanId / byClanName),
 *   member_deleted (target), moderated (action; target, from, to where they apply).
 */
export interface ClanEvent {
  /** Increases by one per event of the clan (the history cursor). */
  seq: number;
  at: Timestamp;
  type: ClanEventType;
  actorId: PlayerId | null;
  actorName: string | null;
  targetId: PlayerId | null;
  targetName: string | null;
  zone?: number;
  stage?: number;
  /** Zones the member held when it left the clan (they leave the clan with it). */
  zones?: number[];
  role?: ClanRole;
  from?: string;
  to?: string;
  emblem?: number;
  joinPolicy?: ClanJoinPolicy;
  description?: boolean;
  via?: ClanJoinPolicy;
  byId?: PlayerId | null;
  byName?: string | null;
  byClanId?: number | null;
  byClanName?: string | null;
  action?: ClanModerationAction;
  reason?: 'account_deleted' | 'moderated';
}

/** Why clan.membership reached the player. */
/** 'declined': a request of the player ended without it joining (declined, or that clan was disbanded); its clan is unchanged. */
export type ClanMembershipReason = 'created' | 'joined' | 'accepted' | 'left' | 'kicked' | 'disbanded' | 'role' | 'leader' | 'declined';

/** clan.mine: the player's clan with the player's role, its own requests and what it may do. */
export interface ClanMineResult {
  clan: ClanInfo | null;
  role: ClanRole | null;
  /** The player's own pending requests (a player without a clan), oldest first. */
  requests: ClanJoinRequest[];
  /** Leader and officers: the clan's pending requests; 0 for everybody else. */
  pendingRequests: number;
  /** After leaving or being kicked: the player may join a clan again from then on (null = now). */
  rejoinAt: Timestamp | null;
  /** Whether clan.create would be accepted now; if not, the refusal's reason and (cooldown) until when. */
  canCreate: { ok: boolean; reason?: string; at?: Timestamp };
}

// ---------------------------------------------------------------------------------------------------------
// Auction: the original "king of the hill" for the Annihilator (menu_auc_rules), paid in diamonds (design D7)

export type Currency = 'diam' | 'gold';

/** A player who held the Annihilator for a whole round without being outbid: one permanent copy per entry. */
export interface AuctionWinner {
  playerId: PlayerId;
  displayName: string;
  /** When the round ended and the copy became permanent (epoch ms). */
  wonAt: Timestamp;
  /** Diamonds the winner paid for it. */
  price: number;
}

/**
 * The public state of the annihilator auction (auction.get, the auction.updated event). Rules: anybody but the holder
 * buys at `price` (diamonds from the canonical game state); the price then goes up by 1 and the buyer becomes the
 * holder, the previous holder loses it without a refund. The holder has one Annihilator in every battle. A holder
 * nobody outbids until `endsAt` (the purchase + `roundMs`, 7 days) keeps a copy for good (`winners`), and a new round
 * starts at `startPrice` without a holder.
 */
export interface AuctionState {
  /** The weapon sold ('annihilator'). */
  itemId: string;
  /** Price of the next purchase in diamonds. */
  price: number;
  /** Price of the first purchase of a round. */
  startPrice: number;
  /** The last buyer of this round; null before the first purchase of a round. */
  holder: { playerId: PlayerId; displayName: string } | null;
  /** When the holder bought it (epoch ms); null without a holder. */
  since: Timestamp | null;
  /** When the holder keeps a copy for good unless somebody buys before (epoch ms); null without a holder. */
  endsAt: Timestamp | null;
  /** Round number, 1 = the first round. */
  round: number;
  /** How long a holder must stay unchallenged (ms). */
  roundMs: number;
  /** Purchases in this round. */
  purchases: number;
  /** Winners of the earlier rounds, newest first (the latest ones; `winnerCount` counts all). */
  winners: AuctionWinner[];
  winnerCount: number;
}

/** The Annihilators of one player from the auction: the held one (0/1) plus the permanent copies. */
export interface AuctionHoldings {
  held: number;
  permanent: number;
  total: number;
}

/** `auction.get` result: the auction and what the asking player holds. */
export interface AuctionInfo extends AuctionState {
  mine: AuctionHoldings;
  serverTime: Timestamp;
}

/** `player.command` 'auction.buy' arguments: the price the player saw (another price: VALIDATION_FAILED 'price_changed'). */
export interface AuctionBuyCommandArgs {
  expectedPrice: number;
}

/** `player.command` 'auction.buy' result: the diamonds paid and the auction after the purchase. */
export interface AuctionBuyCommandResult {
  paid: number;
  auction: AuctionState;
  mine: AuctionHoldings;
}

// ---------------------------------------------------------------------------------------------------------
// News (optional operator notes)

export interface NewsItem {
  newsId: string;
  title: string;
  body: string;
  publishedAt: Timestamp;
}

// ---------------------------------------------------------------------------------------------------------
// Battles, rooms, sessions, replays

export type BattleMode = 'career' | 'missions' | 'survival' | 'boss' | 'assault' | 'just';
export const BATTLE_MODES: readonly BattleMode[] = ['career', 'missions', 'survival', 'boss', 'assault', 'just'];

export interface BattleResult {
  /** Winning team (1-based); 0 = cancelled / no winner (`end_game_fn(0)`). */
  winnerTeam: number;
}

export interface RoomTeamSettings {
  /** 1..8 worms (new_interf.as:3357). */
  worms: number;
  /** Team colour index 1..7 (proc.game_get_color). */
  color: number;
  name: string;
}

/**
 * Settings of a "just play" battle, modelled after `new_interf.barracks` (new_interf.as:168-197). A room is its own
 * mode: every PvP map (ONLINE_MAPS of src/session/online.ts) is open to every host, whatever its career.
 */
export interface RoomSettings {
  /** The map: the stable id of a PvP map (ONLINE_MAP_IDS: 'wormville1' .. 'sheovar5'). */
  mapId: OnlineMapId;
  /**
   * The same map's number in the PvP list ("Карта №N", 1..25; 1..5 are the maps of the original "Просто играть").
   * The field predates mapId (protocol 1: a field never disappears): a room always carries both.
   */
  mapIndex: number;
  /** 2..4 teams, at most 12 worms in total. */
  teams: RoomTeamSettings[];
}

/**
 * RoomSettings as a request (room.create / room.update) gives them: the map by `mapId` or by `mapIndex` (a client of
 * an older version), or by both when they name the same map. The server stores and returns both (normalizeRoomSettings).
 */
export type RoomSettingsInput = Omit<RoomSettings, 'mapId' | 'mapIndex'> & { mapId?: string; mapIndex?: number };

/** `s` with both map fields of the map it names (older stored rooms name it by mapIndex only); null for an unknown map. */
export function normalizeRoomSettings(s: RoomSettingsInput): RoomSettings | null {
  const m = onlineMapOf(s);
  return m ? { mapId: m.mapId, mapIndex: m.mapIndex, teams: s.teams } : null;
}

/** Everything needed to start (and re-simulate) a battle deterministically, together with the seed. */
export interface BattleInit {
  mode: BattleMode;
  /** play_id: stage / mission / zone number (proc.init_gameplay param4). */
  stage: number;
  mapId?: string;
  /** Assault zone and its stage at battle start (scales enemies). */
  zone?: number;
  zoneStage?: number;
  /** settings.skill (difficulty, +10 = hardcore). */
  skill?: number;
  /** The room's settings of an online battle (both map fields; a record of an older version has mapIndex only). */
  teams?: RoomSettingsInput;
  /** Opaque game-specific parameters (e.g. the player's team as built by init_game). */
  params?: JsonValue;
}

export interface RoomPlayer {
  playerId: PlayerId;
  displayName: string;
  /** Team index (1-based) this player controls. */
  team: number;
  ready: boolean;
  connected: boolean;
  /**
   * When the player lost its last connection (null while connected). The player keeps its slot and ready flag for the
   * server's disconnect grace period (DISCONNECT_GRACE, default 60 s), then leaves the lobby.
   */
  disconnectedAt: Timestamp | null;
}

export interface Room {
  roomId: string;
  /** Join code for private rooms (null for public rooms). */
  code: string | null;
  hostId: PlayerId;
  mode: 'just';
  private: boolean;
  settings: RoomSettings;
  players: RoomPlayer[];
  maxPlayers: number;
  state: 'lobby' | 'in_game' | 'closed';
  sessionId: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  /** Monotonic version of the room: +1 with every change (room.updated carries the room with it). */
  version: number;
}

export interface MatchTicket {
  ticketId: string;
  mode: 'just';
  teams: number;
  wormsPerTeam: number;
  levelBucket: number;
  queuedAt: Timestamp;
}

export interface SessionParticipant {
  playerId: PlayerId;
  displayName: string;
  teams: number[];
  connected: boolean;
  /** When the participant lost its last connection (null while connected); its turns are forced after the grace. */
  disconnectedAt: Timestamp | null;
  finished: boolean;
  forfeited: boolean;
}

export type ValidationVerdict = 'verified' | 'rejected' | 'unverified';

export interface SessionOutcome {
  result: BattleResult;
  /** True only when an authoritative validator re-simulated the battle and confirmed the result. */
  verified: boolean;
  verdict: ValidationVerdict;
  reason: string | null;
  /** True when every finishing participant reported the same result and final hash. */
  consensus: boolean;
  replayId: string | null;
  endedAt: Timestamp;
  /**
   * A battle ended by a dispute (session.dispute): the participants whose checkpoints or turn ends the server's own
   * simulation of the battle contradicted (their game went out of sync, or they lied). Absent for other endings.
   */
  diverged?: PlayerId[];
}

export interface SessionInfo {
  sessionId: string;
  mode: BattleMode;
  stage: number;
  roomId: string | null;
  /** Initial `cur_random` index into RANDOM_NUMBERS (0..399), issued by the backend (proc.as:494). */
  seed: number;
  /** TURNTIME (45 or 20 s, proc.as:472-479). */
  turnTimeSec: number;
  /**
   * The battle's init in the general shape (mode, stage, map, teams). An online (room) session has the exact init in
   * `online` (its replay stores it as `init.params`; the live session's `init` has no `params`).
   */
  init: BattleInit;
  /**
   * Online (room) session: everything the battle is built from besides `seed` - every client and the server's battle
   * simulator build the same battle from it in the neutral battle context (design D5). Null for a solo session.
   */
  online: OnlineBattleInit | null;
  participants: SessionParticipant[];
  /** Teams in play, in turn order. */
  teamOrder: number[];
  state: 'active' | 'ended';
  turn: number;
  currentTeam: number;
  turnStartTick: number;
  /**
   * The server closed the current turn's input after this tick (a forced turn: the owner missed its deadline,
   * disconnected beyond the grace period or forfeited); the next team is reported by a participant (session.endTurn
   * with `forcedEnd`). Null otherwise.
   */
  inputClosedAt: number | null;
  /**
   * Wall-clock deadline of the current turn (null for solo sessions); while the input is closed: until when the
   * server waits for the report of the forced turn's end.
   */
  deadlineAt: Timestamp | null;
  createdAt: Timestamp;
  outcome: SessionOutcome | null;
  /** Monotonic version of the session: +1 with every change (every session event carries it). */
  version: number;
}

/** A turn of a session's command log without its input (session.get `turns`). */
export interface SessionTurnSummary {
  turn: number;
  team: number;
  startTick: number;
  /** Null while the turn is open. */
  endTick: number | null;
  /** Last tick whose input is final. */
  throughTick: number;
  /** A forced turn: its input was closed after this tick (TurnRecord.forcedAt); null otherwise. */
  forcedAt: number | null;
  commands: number;
}

/** A state hash every reporting participant agreed on (session.get `hashes`): a checkpoint a rebuild can verify. */
export interface SessionCheckpoint {
  tick: number;
  hash: string;
}

/**
 * Where session.finish leaves the session: 'waiting' for the other participants' reports, 'verifying' (the server
 * re-simulates the battle; the verdict arrives as session.ended) or 'ended' (the outcome is in the response).
 */
export type SessionFinishStatus = 'waiting' | 'verifying' | 'ended';

/** session.dispute: the server checks the disputed battle ('resolving': the verdict comes as session.ended). */
export type SessionDisputeStatus = 'resolving' | 'ended';

export interface ReplayRecord {
  replayId: string;
  playerId: PlayerId;
  createdAt: Timestamp;
  mode: BattleMode;
  stage: number;
  result: BattleResult;
  sessionId: string | null;
  seed: number | null;
  init: BattleInit | null;
  log: CommandLogData | null;
  /** Legacy `!PARAMS!ROUNDS!chk!` string, when available. */
  legacy: string | null;
  verified: boolean;
  logHash: string | null;
  /** How the server validated the battle (replays of `battle.finish` and of sessions; absent in older records). */
  verdict?: ValidationVerdict;
  /** The `battle.start` battle this replay records (solo battles of player.command). */
  battleId?: string;
}

export type ReplaySummary = Pick<ReplayRecord, 'replayId' | 'playerId' | 'createdAt' | 'mode' | 'stage' | 'result' | 'verified' | 'sessionId' | 'verdict' | 'battleId'>;

/** Default "just play" settings = the original barracks defaults (new_interf.as:168-197). */
export function defaultRoomSettings(): RoomSettings {
  return {
    mapId: DEFAULT_ONLINE_MAP.mapId,
    mapIndex: DEFAULT_ONLINE_MAP.mapIndex,
    teams: [
      { worms: 1, color: 2, name: 'Команда #1' },
      { worms: 1, color: 1, name: 'Команда #2' },
    ],
  };
}

// ------------------------------------------------------------------------------------------------ store and ads
// In-app purchases (store.catalog, player.command 'store.redeem') and opt-in rewarded ads (ads.*). The catalogue of
// products and placements is the server's (src/server/core/monetization-catalog.ts); prices live in the platform's
// store console (Google Play Console now, App Store Connect later), never on the server.

/** Where a purchase was made: Google Play Billing now ('app-store' later); 'dev-test' = the development provider. */
export type StoreProviderId = 'google-play' | 'app-store' | 'dev-test';
export const STORE_PROVIDERS: readonly StoreProviderId[] = ['google-play', 'app-store', 'dev-test'];

/** A product of the store as the asking player sees it (store.catalog). */
export interface StoreProductInfo {
  /** The store's product id (the same id in Google Play Console and, later, App Store Connect). */
  productId: string;
  /** 'currency_pack' = gold or diamonds; 'bundle' = several things (the starter pack). */
  kind: 'currency_pack' | 'bundle';
  /** What one purchase credits (items: stimulator id -> count). */
  gold: number;
  diam: number;
  items: { [itemId: string]: number };
  /** The next purchase of it is credited this many times (the first purchase x2); 1 = no bonus. */
  multiplier: number;
  /** Purchases per account (null = unlimited) and how many this account made. */
  limitPerAccount: number | null;
  purchased: number;
  /** Whether the player may buy it now (sale window, level, the per-account limit). Clients offer only these. */
  available: boolean;
  /** Where the in-game shop shows it: tab 'diamonds' | 'gold' (slot 1..6) or 'offer'; null = not in the game. */
  shop: { tab: 'diamonds' | 'gold' | 'offer'; slot: number } | null;
  /** A badge: 'popular' | 'best_value' | null. */
  label: 'popular' | 'best_value' | null;
  /** End of the offer's current window (epoch ms), null = none. */
  availableUntil: Timestamp | null;
  /**
   * The owner's price list (ISO 4217 code -> price; USD and UAH always): what a client shows where the platform store
   * reports no price of its own (no store on this device or server: the offers are shown, nothing is sold). The price
   * a player pays is always the platform store's.
   */
  listPrices: { [currency: string]: number };
  /** The pack's bonus over the base rate in percent (the shop's badge "+20 %"; 0 = none; a bundle: its whole value). */
  bonusPct: number;
}

export interface StoreCatalogResult {
  /** Providers this server verifies purchases of (empty: no purchases here; the products are then only shown). */
  providers: StoreProviderId[];
  products: StoreProductInfo[];
  /**
   * Pass it to the store with the purchase (Play Billing: BillingFlowParams.setObfuscatedAccountId; App Store:
   * appAccountToken later): the server credits a purchase it learns of by itself (Google's real-time notification) to
   * this account, and refuses to credit it to another. A keyed hash: it identifies nobody outside this server.
   */
  obfuscatedAccountId: string;
}

/** player.command 'store.redeem' result. */
export interface StoreRedeemCommandResult {
  /** 'credited' = credited now; 'already_credited' = this purchase was credited to this player before (nothing added). */
  status: 'credited' | 'already_credited';
  /** The server's id of the purchase (a hash of the provider and the purchase token). */
  purchaseId: string;
  productId: string;
  /** What the purchase credited (both statuses; the first-purchase multiplier included). */
  gold: number;
  diam: number;
  items: { [itemId: string]: number };
  /** The multiplier that was applied (2 = the first purchase x2). */
  multiplier: number;
  /** The store's test purchase (license testers): credited like a real one. */
  test: boolean;
  /** true: consume the purchase with the platform billing library now (the server consumes it too). */
  consume: boolean;
}

/** Who shows the rewarded ads: AdMob (with server-side verification); 'dev-test' = the development provider. */
export type AdProviderId = 'admob' | 'dev-test';
export const AD_PROVIDERS: readonly AdProviderId[] = ['admob', 'dev-test'];

/** An opt-in rewarded ad placement of the game. */
export type AdPlacement = 'ad_battle_double' | 'ad_grave_extra' | 'ad_darts_free' | 'ad_daily_gift_x2' | 'ad_rest';
export const AD_PLACEMENT_LIST: readonly AdPlacement[] = ['ad_battle_double', 'ad_grave_extra', 'ad_darts_free', 'ad_daily_gift_x2', 'ad_rest'];

export interface AdPlacementStatus {
  placement: AdPlacement;
  /** The server offers it at all. */
  enabled: boolean;
  dailyCap: number;
  /** Rewards (and open tickets) of the player's current day. */
  usedToday: number;
  remainingToday: number;
  /** The largest reward of one view (ad_battle_double: gold), null = none. */
  maxAmount: number | null;
}

export interface AdsStatusResult {
  providers: AdProviderId[];
  placements: AdPlacementStatus[];
  /** All placements together: the daily cap and what is left today. */
  dailyCapTotal: number;
  remainingToday: number;
  /** When the player's day (and the daily caps) start again, epoch ms. */
  resetsAt: Timestamp;
}

/**
 * What an ad reward is for (ads.start `context`): the won battle (battleId: ad_battle_double, ad_grave_extra) and,
 * for ad_grave_extra, the grave (1-based index of the last won battle's graves).
 */
export interface AdContext {
  battleId?: string;
  index?: number;
}

/**
 * One rewarded ad view (ads.start -> the ad -> the provider's verification -> the server grants the reward).
 * 'open': waiting for the verification; 'granted': the reward is in the game state (`result`); 'failed': verified
 * but the reward could not be given (`failure`); 'expired': no verification came in time.
 */
export interface AdTicketInfo {
  ticketId: string;
  placement: AdPlacement;
  provider: AdProviderId;
  status: 'open' | 'granted' | 'failed' | 'expired';
  createdAt: Timestamp;
  expiresAt: Timestamp;
  /** Pass to the ad SDK as its server-side verification options (AdMob ServerSideVerificationOptions). */
  ssv: { userId: string; customData: string };
  /** The reward that was granted (placement-specific, see the placement). */
  result: { [key: string]: JsonValue } | null;
  failure: string | null;
  grantedAt: Timestamp | null;
}

/** Whether the account is linked to a Google Play Games player (auth.playGamesStatus). */
export interface PlayGamesStatusResult {
  /** The server can verify Play Games sign-ins (its OAuth client is configured). */
  available: boolean;
  linked: boolean;
}

/** Languages of the server's messages to a player (e-mails, push notifications). */
export type MessageLanguage = 'uk' | 'en' | 'ru';
export const MESSAGE_LANGUAGES: readonly MessageLanguage[] = ['uk', 'en', 'ru'];

/**
 * The account's e-mail sign-in (auth.emailStatus, auth.emailChange, auth.emailResend): `email` is the confirmed address
 * the account signs in with (null: none yet), `pendingEmail` an address waiting for the confirmation link (null: none).
 */
export interface EmailStatusResult {
  /** The server sends e-mails (feature 'email': a mail provider and the link address are configured). */
  available: boolean;
  email: string | null;
  pendingEmail: string | null;
}

/** auth.emailVerify: the confirmed address, partly hidden (the link may be opened by anybody who has it). */
export interface EmailVerifiedResult {
  verified: true;
  email: string;
}

/** The attestation service of an integrity token (integrity.verify provider): 'play' = Google Play Integrity. */
export type IntegrityProvider = 'play';
export const INTEGRITY_PROVIDERS: readonly IntegrityProvider[] = ['play'];

/** A one-time challenge of a Play Integrity check (integrity.challenge): the app passes it as the request hash. */
export interface IntegrityChallenge {
  challenge: string;
  expiresAt: Timestamp;
}

/**
 * The server's reading of a Play Integrity verdict (integrity.verify), recorded for the player as a signal (never a
 * refusal of its own): 'ok' = a genuine install from Google Play on a device that meets integrity, 'suspicious' = some
 * check failed, 'failed' = the token was unreadable, stale or not for this challenge.
 */
export interface IntegrityResult {
  verdict: 'ok' | 'suspicious' | 'failed';
  checks: { app: boolean; device: boolean; licensed: boolean; fresh: boolean };
  at: Timestamp;
}

/** Push notification devices of the account (push.register / push.unregister). */
export interface PushDevicesResult {
  devices: number;
}

// ------------------------------------------------------------------------------------------------ chat

/**
 * A chat channel: 'global' (every signed-in player reads it) or 'clan:<clanId>' (the members of that clan only). The
 * rules of the channels and of a message's text: src/shared/chat-rules.ts; the numbers: system.hello limits.chat.
 */
export type ChatChannelKind = 'global' | 'clan';
export const CHAT_CHANNEL_KINDS: readonly ChatChannelKind[] = ['global', 'clan'];

/** One chat message as every client shows it: plain text, never markup. */
export interface ChatMessage {
  messageId: string;
  channel: string;
  /** Position in the channel: strictly increasing within the server (chat.markRead takes it). */
  seq: number;
  /** When the server took the message. */
  at: Timestamp;
  playerId: PlayerId;
  /** The sender's nickname (as it is now: a rename changes the kept messages too). */
  nickname: string;
  /** The sender's level when it wrote the message. */
  level: number;
  /** The clan the sender was in when it wrote the message, as the clan is now (null once the clan is gone). */
  clan: ClanBrief | null;
  /** The sanitized text, the operator's filter words masked with `*`. */
  text: string;
}

/**
 * Why a player cannot write to a channel it reads: 'guest' (a guest account reads only; sign up with a nickname),
 * 'level' (below limits.chat.minLevelToSend), 'muted' (an operator muted the player until `mutedUntil`).
 */
export type ChatSendBlock = 'guest' | 'level' | 'muted';
export const CHAT_SEND_BLOCKS: readonly ChatSendBlock[] = ['guest', 'level', 'muted'];

/** A channel the asking player may read (chat.channels). */
export interface ChatChannelInfo {
  channel: string;
  kind: ChatChannelKind;
  /** The clan of a clan channel (null for 'global'). */
  clan: ClanBrief | null;
  canSend: boolean;
  sendBlock: ChatSendBlock | null;
  /** seq of the newest message (0: none yet). */
  lastSeq: number;
  /** The player's read position (chat.markRead); messages after it, not its own, are `unread`. */
  readSeq: number;
  unread: number;
  /** Whether the player's connections get the channel's chat.message events (chat.subscribe). */
  subscribed: boolean;
}

/** Why messages left a channel (chat.deleted): an operator removed them, or their sender's account was deleted. */
export type ChatDeleteReason = 'moderator' | 'account_deleted';
export const CHAT_DELETE_REASONS: readonly ChatDeleteReason[] = ['moderator', 'account_deleted'];

/** system.hello limits.chat: what a client checks before it sends a message. */
export interface ChatLimits {
  /** Longest text after sanitization (code points). */
  textMax: number;
  /** Messages each channel keeps (the oldest go first). */
  historyGlobal: number;
  historyClan: number;
  /** Writing needs a registered account (guests read only) of at least this level. */
  minLevelToSend: number;
  guestsCanSend: boolean;
  /** At most `burst` messages per `windowMs`, at least `minIntervalMs` apart. */
  burst: number;
  windowMs: number;
  minIntervalMs: number;
  /** The same text again within this period is refused (RATE_LIMITED 'chat_duplicate'). */
  duplicateWindowMs: number;
}
